import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BarChart3, Eye, Users, MessageCircle, Ticket, Mail, Send, RefreshCw, TrendingUp } from "lucide-react";

const PERIODS = [
  { value: 1,  label: "Aujourd'hui" },
  { value: 7,  label: "7 jours" },
  { value: 30, label: "30 jours" },
  { value: 90, label: "90 jours" },
];

const fmtNum = (n) => new Intl.NumberFormat("fr-FR").format(Number(n) || 0);
const fmtDay = (d) => {
  try {
    const date = new Date(d);
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch { return d; }
};

// Petit graphique en barres pur CSS
const MiniChart = ({ data, valueKey = "visits" }) => {
  if (!data || data.length === 0) return <p className="text-white/40 text-xs text-center py-6">Aucune donnée pour cette période.</p>;
  const max = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  return (
    <div className="flex items-end gap-1.5 h-32 px-1">
      {data.map((d, i) => {
        const h = Math.max(2, ((Number(d[valueKey]) || 0) / max) * 100);
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group min-w-0">
            <div className="text-[9px] text-yellow-300 font-mono opacity-0 group-hover:opacity-100 transition">{fmtNum(d[valueKey])}</div>
            <div
              className="w-full bg-gradient-to-t from-yellow-500 to-yellow-300 rounded-t transition-all hover:from-yellow-400 hover:to-yellow-200"
              style={{ height: `${h}%` }}
            />
            <div className="text-[9px] text-white/45 font-mono truncate w-full text-center">{fmtDay(d.day)}</div>
          </div>
        );
      })}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent = "yellow", testId }) => (
  <div className={`rounded-2xl border border-${accent}-400/20 bg-${accent}-400/[0.04] p-4`} data-testid={testId}>
    <div className="flex items-center gap-2 text-white/55 text-[10px] uppercase tracking-widest font-bold mb-2">
      <Icon size={13} className={`text-${accent}-300`} /> {label}
    </div>
    <p className="text-3xl font-black tabular-nums" style={{ fontFamily: "'Anton', sans-serif" }}>{fmtNum(value)}</p>
    {sub && <p className="text-[11px] text-white/45 mt-1">{sub}</p>}
  </div>
);

export const ArnakStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = async (days = period) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/arnak/stats?period=${days}`);
      setStats(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  return (
    <div className="space-y-6" data-testid="arnak-stats-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold flex items-center gap-2">
            <BarChart3 size={13} /> Statistiques ARNAK
          </p>
          <h2 className="font-black text-2xl md:text-3xl tracking-tight mt-1">Tableau de bord</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white/[0.04] border border-white/10 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest rounded transition ${period === p.value ? "bg-yellow-400 text-black" : "text-white/55 hover:text-white"}`}
                data-testid={`stats-period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => load()} className="p-2.5 bg-white/5 hover:bg-white/10 rounded-lg transition" title="Rafraîchir">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {!stats ? (
        <p className="text-center py-12 text-white/40 text-sm">Chargement…</p>
      ) : (
        <>
          {/* KPIs principaux */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Eye}        label="Visites"        value={stats.visits.total}  sub={`${fmtNum(stats.visits.unique)} visiteurs uniques`} testId="kpi-visits" />
            <StatCard icon={Users}      label="Membres actifs" value={stats.active_users}  sub="Ont posté au chat" testId="kpi-active" />
            <StatCard icon={MessageCircle} label="Messages chat" value={stats.chat.total} sub={`${stats.chat.by_channel.length} salons`} testId="kpi-chat" />
            <StatCard icon={Mail}       label="Newsletter"     value={stats.newsletter.subscribers} sub={`+${fmtNum(stats.newsletter.new_in_period)} sur la période`} testId="kpi-newsletter" />
            <StatCard icon={Send}       label="Messages privés (DM)" value={stats.dm?.total ?? 0} sub={`${fmtNum(stats.dm?.active_conversations ?? 0)} conversations actives`} testId="kpi-dm" />
            <StatCard icon={Ticket}     label="Réservations"   value={(stats.tickets || []).reduce((a, t) => a + (Number(t.reservations) || 0), 0)} sub="Tous concerts confondus" testId="kpi-tickets" />
            <StatCard icon={TrendingUp} label="Vue moyenne"    value={stats.visits.by_day.length ? Math.round(stats.visits.total / stats.visits.by_day.length) : 0} sub="par jour" testId="kpi-avg" />
            <StatCard icon={Eye}        label="Top page"       value={stats.visits.top_paths?.[0]?.hits ?? 0} sub={stats.visits.top_paths?.[0]?.path ?? "—"} testId="kpi-toppage" />
          </div>

          {/* Graphique visites par jour */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black uppercase tracking-widest text-yellow-400">Visites par jour</h3>
              <span className="text-[10px] text-white/45 uppercase tracking-widest">{stats.period_days} dernier(s) jour(s)</span>
            </div>
            <MiniChart data={stats.visits.by_day} valueKey="visits" />
          </div>

          {/* Top pages + Chat par salon */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <h3 className="text-sm font-black uppercase tracking-widest text-yellow-400 mb-4">Top pages</h3>
              {stats.visits.top_paths?.length ? (
                <ul className="space-y-2">
                  {stats.visits.top_paths.map((p, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded bg-white/[0.03] hover:bg-white/[0.07] transition">
                      <span className="font-mono text-[12px] truncate text-white/85">{p.path}</span>
                      <span className="text-yellow-300 font-black tabular-nums text-xs shrink-0">{fmtNum(p.hits)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-white/40 text-xs">Pas de visite trackée.</p>}
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <h3 className="text-sm font-black uppercase tracking-widest text-yellow-400 mb-4">Activité chat par salon</h3>
              {stats.chat.by_channel?.length ? (
                <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {stats.chat.by_channel.map((c) => (
                    <li key={c.slug} className="flex items-center justify-between gap-3 px-3 py-2 rounded bg-white/[0.03]">
                      <span className="truncate text-sm"><span className="mr-1.5">{c.emoji}</span>{c.channel}</span>
                      <span className="text-yellow-300 font-black tabular-nums text-xs shrink-0">{fmtNum(c.messages)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-white/40 text-xs">Pas encore de message.</p>}
            </div>
          </div>

          {/* Tickets concerts */}
          {stats.tickets?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <h3 className="text-sm font-black uppercase tracking-widest text-yellow-400 mb-4">Réservations par concert</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-white/45 border-b border-white/10">
                      <th className="py-2 pr-3">Concert</th>
                      <th className="py-2 px-2 text-right">Réservations</th>
                      <th className="py-2 px-2 text-right">Confirmées</th>
                      <th className="py-2 pl-2 text-right">Payées</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.tickets.map((t, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03]">
                        <td className="py-2.5 pr-3 truncate max-w-[280px]">{t.event_title}</td>
                        <td className="py-2.5 px-2 text-right font-mono tabular-nums">{fmtNum(t.reservations)}</td>
                        <td className="py-2.5 px-2 text-right font-mono tabular-nums text-blue-300">{fmtNum(t.confirmed)}</td>
                        <td className="py-2.5 pl-2 text-right font-mono tabular-nums text-yellow-300">{fmtNum(t.paid)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArnakStatsPanel;
